import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MaterialsQaqcDto, ToolsService, UpdateMaterialRequest, UpdateMaterialRequestQaqc } from '../../../services/tools.service';

export interface EditMaterialDialogData {
  material: MaterialsQaqcDto;
}

@Component({
  selector: 'app-edit-material-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    MatSelectModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatNativeDateModule
  ],
  templateUrl: './edit-material-dialog.component.html',
  styleUrls: ['./edit-material-dialog.component.css']
})
export class EditMaterialDialogComponent implements OnInit {
  // Form fields
  cisProContainerId = '';
  specificGravity: number | null = null;
  ph: number | null = null;
  chlorides: number | null = null;
  sampleRefNum = '';
  recvAmount: number | null = null;
  selectedContainerWeight = '1 Gal';
  currentAmount: number | null = null;
  minAmount: number | null = null;
  rigName = '';
  recvDate: string = ''; // Changed to string for input type="date"
  comments = '';
  notifyAtReorder = false;
  trackSample = false;
  otherWeight: number | null = null;

  // Formatted string values for display
  specificGravityDisplay = '';
  phDisplay = '';
  chloridesDisplay = '';
  recvAmountDisplay = '';
  currentAmountDisplay = '';
  minAmountDisplay = '';

  // Track previous container weight for calculations
  private previousContWeightAmount: number = 0;

  // Material info
  materialName = '';
  materialId = 0;
  materialType = 0;

  // Material type enum values for liquid check
  private readonly liquidTypes = [2, 4, 9, 10]; // Liquid, LiquidBase, SpacerLiquidBase, SpacerLiquidFractionBase
  private originalRecvAmount: number | null = null;
  // Check if material is liquid type
  get isLiquid(): boolean {
    return this.liquidTypes.includes(this.materialType);
  }

  // Check if "Other" container weight is selected
  get isOtherSelected(): boolean {
    return this.selectedContainerWeight === 'Other';
  }

  // Container weight options matching backend ToolsRepository.GetContainerWeightsAsync
  containerWeights = [
    { name: '5 Gal', weight: 1245 },
    { name: '3 Gal', weight: 1064 },
    { name: '2 Gal', weight: 550 },
    { name: '1 Gal', weight: 300 },
    { name: 'Gal Jug', weight: 110 },
    { name: '1 qt (H)', weight: 100 },
    { name: '1 qt (s)', weight: 100 },
    { name: '1 qt (RD)', weight: 75 },
    { name: '1 pt', weight: 70 },
    { name: '1 pt (S)', weight: 55 },
    { name: '.50 pt', weight: 40 },
    { name: '.25 pt', weight: 25 },
    { name: 'Other', weight: 0 }
  ];

  // Loading state
  isSubmitting = false;
  errorMessage = '';

  constructor(
    public dialogRef: MatDialogRef<EditMaterialDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditMaterialDialogData,
    private toolsService: ToolsService
  ) { }

  ngOnInit(): void {
    this.populateForm();
  }

  /**
   * Format number to fixed decimal places
   */
  private formatDecimal(value: number | null, decimals: number): string {
    if (value === null || value === undefined) return '';
    return value.toFixed(decimals);
  }

  /**
   * Parse string to number, return null if invalid
   */
  private parseNumber(value: string): number | null {
    if (!value || value.trim() === '') return null;
    const num = parseFloat(value);
    return isNaN(num) ? null : num;
  }

  /**
   * Update display values from numeric values
   */
  private updateDisplayValues(): void {
    this.specificGravityDisplay = this.formatDecimal(this.specificGravity, 4);
    this.phDisplay = this.formatDecimal(this.ph, 2);
    this.chloridesDisplay = this.formatDecimal(this.chlorides, 2);
    this.recvAmountDisplay = this.formatDecimal(this.recvAmount, 3);
    this.currentAmountDisplay = this.formatDecimal(this.currentAmount, 3);
    this.minAmountDisplay = this.formatDecimal(this.minAmount, 3);
  }

  /**
   * Handle specific gravity input change
   */
  onSpecificGravityChange(value: string): void {
    this.specificGravity = this.parseNumber(value);
  }

  /**
   * Handle pH input change
   */
  onPhChange(value: string): void {
    this.ph = this.parseNumber(value);
  }

  /**
   * Handle chlorides input change
   */
  onChloridesChange(value: string): void {
    this.chlorides = this.parseNumber(value);
  }

  /**
   * Handle current amount input change
   */
  onCurrentAmountChange(value: string): void {
    this.currentAmount = this.parseNumber(value);
  }

  /**
   * Handle minimum amount input change
   */
  onMinAmountInputChange(value: string): void {
    this.minAmount = this.parseNumber(value);
  }

  /**
   * Populate form fields with material data
   */
  private populateForm(): void {
    const material = this.data.material;

    this.materialName = material.name;
    this.materialId = material.id;
    this.materialType = material.type;
    this.cisProContainerId = material.cisProContainerId || '';
    this.specificGravity = material.specificGravity ?? null;
    this.ph = material.ph ?? null;
    this.chlorides = material.chlorides ?? null;
    this.sampleRefNum = material.sampleRefNum || '';
    this.recvAmount = material.recvAmount ?? null;
    this.originalRecvAmount = material.recvAmount ?? null;
    this.currentAmount = material.currentAmount ?? null;
    this.minAmount = material.minAmount ?? null;
    this.rigName = material.rigName || '';
    this.comments = material.comments || '';
    this.notifyAtReorder = material.notifyAtReorder ?? false;
    this.trackSample = material.trackSample ?? false;
    this.otherWeight = material.otherWeight ?? null;

    // Set container weight by matching contWeightAmount to weight value
    // Default to empty if not found
    if (material.contWeightAmount !== null && material.contWeightAmount !== undefined && material.contWeightAmount > 0) {
      const matchedContainer = this.containerWeights.find(cw => cw.weight === material.contWeightAmount);
      this.selectedContainerWeight = matchedContainer?.name ?? '';
    } else {
      this.selectedContainerWeight = '';
    }

    // Store previous container weight for calculations
    const selectedWeight = this.containerWeights.find(cw => cw.name === this.selectedContainerWeight);
    this.previousContWeightAmount = selectedWeight?.weight ?? 0;

    // Parse date if exists - format as YYYY-MM-DD for input type="date"
    if (material.recvDate) {
      const date = new Date(material.recvDate);
      this.recvDate = date.toISOString().split('T')[0];
    }

    // Only show comments if they exist, don't set default
    // If there is a comment, display it; if not, leave it empty
    this.comments = material.comments || '';

    // Update formatted display values
    this.updateDisplayValues();
  }
  /**
   * Handle container weight selection change
   * Based on ToolsRepository.cs logic for ContWeightAmount changes
   */
  onContainerWeightChange(): void {
    const selectedWeight = this.containerWeights.find(cw => cw.name === this.selectedContainerWeight);

    if (!selectedWeight) return;

    const newContWeightAmount = selectedWeight.weight;

    // If "Other" is selected, clear the container weight amount and let user enter otherWeight
    if (this.isOtherSelected) {
      if (this.recvAmount !== null && this.recvAmount > 0) {
        // Restore amount before container deduction
        this.recvAmount =
          this.recvAmount + this.previousContWeightAmount;
        // IMPORTANT
        this.originalRecvAmount = this.recvAmount;
        this.minAmount = parseFloat(
          (this.recvAmount / 3).toFixed(3)
        );
        this.recvAmountDisplay = this.formatDecimal(
          this.recvAmount,
          3
        );
        this.minAmountDisplay = this.formatDecimal(
          this.minAmount,
          3
        );
      }
      this.previousContWeightAmount = 0;
      this.otherWeight = null;
      return;
    }

    // Clear otherWeight when switching from "Other" to a specific container
    this.otherWeight = null;

    // Adjust RecvAmount based on container weight change (matching backend logic)
    if (this.recvAmount !== null && this.recvAmount > 0) {
      this.recvAmount =
        this.recvAmount +
        this.previousContWeightAmount -
        newContWeightAmount;
      this.minAmount = parseFloat(
        (this.recvAmount / 3).toFixed(3)
      );
      this.recvAmountDisplay = this.formatDecimal(
        this.recvAmount,
        3
      );
      this.minAmountDisplay = this.formatDecimal(
        this.minAmount,
        3
      );
    }

    // Update previous for next change
    this.previousContWeightAmount = newContWeightAmount;
  }

  /**
   * Handle other weight change
   */
  onOtherWeightChange(): void {
   if (
      this.originalRecvAmount === null ||
      this.otherWeight === null
    ) {
      return;
    }
    this.recvAmount =
      this.originalRecvAmount - this.otherWeight;
    this.recvAmountDisplay = this.formatDecimal(
      this.recvAmount,
      3
    );
    this.minAmount = parseFloat(
      (this.recvAmount / 3).toFixed(3)
    );
    this.minAmountDisplay = this.formatDecimal(
      this.minAmount,3);
  }

  /**
   * Handle received amount input change (from text input)
   */
  onRecvAmountInputChange(value: string): void {
    this.recvAmount = this.parseNumber(value);
    this.originalRecvAmount = this.recvAmount;
    this.onRecvAmountChange();
  }

  /**
   * Handle received amount change (calculation)
   */
 onRecvAmountChange(): void {
      if (this.recvAmount === null || this.recvAmount <= 0) {
      return;
      } 
      let adjustedAmount = this.recvAmount;     
 if (
      this.isOtherSelected &&
      this.otherWeight !== null &&
      this.otherWeight > 0
    ) {
      adjustedAmount =this.recvAmount - this.otherWeight;
    }
     
  else {
      const selectedWeight = this.containerWeights.find(
        cw => cw.name === this.selectedContainerWeight
      );
      if (selectedWeight && selectedWeight.weight > 0) {
        adjustedAmount =
          this.recvAmount - selectedWeight.weight;
      }
    }
     
  this.minAmount = parseFloat(
      (adjustedAmount / 3).toFixed(3)
    );
    this.minAmountDisplay = this.formatDecimal(
      this.minAmount,
      3
    );
  }


  /**
   * Check if form can be submitted (validation)
   * Based on WPF reference: CanSubmit() returns Material != null && Material.IsValid()
   */
  get canSubmit(): boolean {
    // Material must exist
    if (!this.materialId || this.materialId === 0) {
      return false;
    }
    // Basic validation - material is valid if it has an id
    return true;
  }

  /**
   * Submit the form
   */
  submit(): void {
    // Check canSubmit before proceeding (matching WPF CanSubmit pattern)
    if (!this.canSubmit) {
      this.errorMessage = 'Cannot submit: Invalid material data.';
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';

    const selectedWeight = this.containerWeights.find(cw => cw.name === this.selectedContainerWeight);

    const request: UpdateMaterialRequestQaqc = {
      id: this.materialId,
      cisProContainerId: this.cisProContainerId,
      specificGravity: this.specificGravity,
      ph: this.isLiquid ? this.ph : null,
      chlorides: this.isLiquid ? this.chlorides : null,
      sampleRefNum: this.sampleRefNum,
      recvAmount: this.recvAmount,
      currentAmount: this.currentAmount,
      minAmount: this.minAmount,
      rigName: this.rigName,
      recvDate: this.recvDate ? this.recvDate : null,
      comments: this.comments,
      notifyAtReorder: this.notifyAtReorder,
      trackSample: this.trackSample,
      contWeightAmount: selectedWeight?.weight ?? 0,
      otherWeight: this.isOtherSelected ? this.otherWeight : null
    };

    this.toolsService.updateMaterialQaqc(request).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.dialogRef.close(true); // Return true to indicate success
      },
      error: (error) => {
        console.error('Error updating material:', error);
        this.errorMessage = 'Failed to update material. Please try again.';
        this.isSubmitting = false;
      }
    });
  }

  /**
   * Cancel and close dialog
   */
  cancel(): void {
    this.dialogRef.close(false);
  }
}
